<script setup>
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import $api from '~/utils/api'
import AddEditPermissionDialog from '~/components/dialogs/AddEditPermissionDialog.vue'
import { useI18n } from 'vue-i18n';

const { t } = useI18n()

definePage({
  meta: {
    action: 'view',
    subject: 'permissionss',
  },
})

// 👉 Заголовки таблицы
const headers = computed(() => [
  { title: t('admin_notifications.name'), key: 'name' },
  { title: t('admin_notifications.assigned_to'), key: 'assignedTo', sortable: false },
  { title: t('admin_notifications.created_date'), key: 'createdDate', sortable: false },
  { title: t('admin_notifications.actions'), key: 'actions', sortable: false },
])

// 👉 Переменные
const search = ref('')
const itemsPerPage = ref(50)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()
const isPermissionDialogVisible = ref(false)
const isAddPermissionDialogVisible = ref(false)
const editingPermission = ref(null)

// 👉 Функция обновления параметров таблицы
const updateOptions = options => {
  page.value = options.page
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// 👉 Цвета ролей
const colors = {
  'pult': { color: 'info', text: 'Pult' },
  'closer': { color: 'success', text: 'Closer' },
  'cold': { color: 'warning', text: 'Colder' },
  'administrator': { color: 'primary', text: 'Administrator' }
}

// 👉 Загружаем пермишены с API
const { data: permissionsData, execute: refresh } = await useApi(createUrl('/api/permissions', {
  query: { q: search, itemsPerPage, page, sortBy, orderBy }
}))

const permissions = computed(() => permissionsData.value.data)
const totalPermissions = computed(() => permissionsData.value.total)

// 👉 Открытие модального окна для создания / редактирования пермишена
const editPermission = permission => {
  editingPermission.value = { ...permission }
  isPermissionDialogVisible.value = true
}

const addPermission = () => {
  editingPermission.value = null
  isAddPermissionDialogVisible.value = true
}

// 👉 Удаление пермишена
const deletePermission = async id => {
  if (confirm(t('admin_notifications.confirm_delete_permission'))) {
    await $api.delete(`/permissions/${id}/delete`)
    refresh() // Перезагружаем данные
  }
}
</script>

<template>
  <VRow>
    <VCol cols="12">
      <h5 class="text-h4 mb-6">{{ $t('admin_notifications.permissions_list') }}</h5>
    </VCol>

    <VCol cols="12">
      <VCard>
        <VCardText class="d-flex align-center justify-space-between flex-wrap gap-4">
          <AppSelect :model-value="itemsPerPage"
            :items="[10, 25, 50, 100].map(i => ({ value: i, title: i.toString() }))" style="inline-size: 5rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)" />
          <div v-if="$can('create', 'permissions')" class="d-flex align-center gap-4 flex-wrap">
            <AppTextField v-model="search" :placeholder="t('admin_notifications.search')" density="compact"
              style="inline-size: 12.5rem;" />
            <VBtn density="default" @click="addPermission">{{ $t('admin_notifications.add_permission') }}</VBtn>
          </div>
        </VCardText>

        <VDivider />

        <VDataTableServer v-model:items-per-page="itemsPerPage" v-model:page="page" :items-length="totalPermissions"
          :headers="headers" :items="permissions" class="text-no-wrap" @update:options="updateOptions">
          <!-- Assigned To -->
          <template #item.assignedTo="{ item }">
            <div class="d-flex gap-2">
              <VChip v-for="text in item.assignedTo" :key="text" label :color="colors[text]?.color"
                class="font-weight-medium">
                {{ colors[text]?.text }}
              </VChip>
            </div>
          </template>

          <template #item.createdDate="{ item }">
            <span>{{ item.created_at }}</span>
          </template>

          <!-- Actions -->
          <template #item.actions="{ item }">
            <VBtn icon size="small" color="medium-emphasis" variant="text" v-if="$can('edit', 'permissions')"
              @click="editPermission(item)">
              <VIcon size="22" icon="tabler-edit" />
            </VBtn>
            <VBtn icon size="small" variant="text" color="medium-emphasis" v-if="$can('delete', 'permissions')"
              @click="deletePermission(item.id)">
              <VIcon size="22" icon="tabler-trash" />
            </VBtn>
          </template>


          <template #bottom>
            <VCardText class="pt-2">
              <div class="d-flex flex-wrap justify-center justify-sm-space-between gap-y-2 mt-2">
                <VSelect v-model="itemsPerPage" :items="[10, 25, 50, 100]" label="Rows per page:" variant="underlined"
                  style="max-inline-size: 8rem;min-inline-size: 5rem;" />

                <VPagination v-model="page" :total-visible="$vuetify.display.smAndDown ? 3 : 5"
                  :length="Math.ceil(totalPermissions / itemsPerPage)" />
              </div>
            </VCardText>
          </template>
        </VDataTableServer>
      </VCard>

      <!-- Диалог создания/редактирования -->
      <AddEditPermissionDialog v-model:isDialogVisible="isPermissionDialogVisible"
        v-model:permission="editingPermission" @refresh="refresh" />
      <AddEditPermissionDialog v-model:isDialogVisible="isAddPermissionDialogVisible" @refresh="refresh" />
    </VCol>
  </VRow>
</template>
